import { Link } from '@ampcore/adel-react'
import { SearchResultItemProps } from '@ampcore/client-side-services'
import React from 'react'
import xss from 'xss'

import { searchUtils } from './SearchHelpers'

const findPropertyValue = (
  entities: Record<string, unknown>[],
  propertyKey: string,
  propertyName: string,
  valueKey: string
): string | undefined => {
  const entity = entities.find(
    e => e[propertyKey] === propertyName && typeof e[valueKey] === 'string'
  )
  if (!entity) return undefined

  const mentions = entity['mentions'] as
    | { text: string; confidence: number }[]
    | undefined
  if (mentions?.length && mentions[0]?.text) {
    return mentions[0].text
  }

  return entity[valueKey] as string | undefined
}

export const SearchResultItem = ({
  result,
  queryTermsRegExp,
  testId
}: SearchResultItemProps & { testId?: string }) => {
  const { properties, metadata, documentPassages, advisorTitle, documentId } =
    result
  const enrichedHtml = properties?.enriched_html?.[0]
  const entities = enrichedHtml?.entities || []
  const fileType =
    properties?.extracted_metadata?.file_type?.toLowerCase() || ''
  const resolvedUrl = xss(metadata?.source?.url ?? '')
  const category = xss(
    findPropertyValue(entities, 'model_name', 'Category', 'text') || ''
  )
  const rawTitle =
    findPropertyValue(entities, 'model_name', 'Omni_Title', 'text') ||
    properties?.extracted_metadata?.title ||
    properties?.title?.split('|')[0] ||
    properties?.extracted_metadata?.filename ||
    ''
  const resolvedTitle = xss(
    searchUtils.helpers.queryMark({
      content: rawTitle,
      queryTermsRegExp
    })
  )

  const rawDescription =
    findPropertyValue(entities, 'model_name', 'Omni_description', 'text') ||
    findPropertyValue(entities, 'model_name', 'description', 'text') ||
    properties?.extracted_metadata?.subject ||
    ''

  const resolvedDescription = xss(
    searchUtils.helpers.queryMark({
      content: rawDescription,
      queryTermsRegExp
    })
  )

  const passage = xss(documentPassages?.[0]?.passageText || '')

  const renderTitle = () => (
    <h3
      className="u-textSizeMsrp4 u-sm-textSizeMsrp3"
      data-testid={testId ? `${testId}-title` : undefined}
    >
      <Link url={resolvedUrl} name={resolvedTitle} />
    </h3>
  )

  const renderDescription = () => (
    <p
      className="u-marginTop4"
      data-testid={testId ? `${testId}-description` : undefined}
      dangerouslySetInnerHTML={{
        __html: resolvedDescription || passage
      }}
    />
  )

  const renderAdvisor = () => (
    <p
      className="u-marginTop4"
      data-testid={testId ? `${testId}-advisor` : undefined}
    >
      {advisorTitle}
    </p>
  )

  const listItemClass =
    fileType === 'pdf'
      ? 'SearchResultItem-pdfListItem u-paddingVert8'
      : 'u-paddingVert8'

  const content =
    category.toLowerCase() === 'financial advisors'
      ? renderAdvisor()
      : renderDescription()

  return (
    <li key={`searchResult-${documentId}`} className={listItemClass}>
      {renderTitle()}
      {content}
    </li>
  )
}
